import { AfterViewInit, ChangeDetectionStrategy, Component, inject, Input, OnDestroy, signal, WritableSignal } from '@angular/core';
import { ModalController, IonButton, IonContent, IonIcon, IonProgressBar } from '@ionic/angular/standalone';
import { NgClass, NgIf } from '@angular/common';
import { StatusBarInfo } from '@capacitor/status-bar';
import { StatusBarService } from '@services/status-bar.service';
import { PlatformService } from '@services/platform.service';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '@environments/environment';
import { UpdateAppService } from '@services/update-app.service';

@Component({
  selector: 'update-app-modal',
  templateUrl: './update-app-modal.component.html',
  styleUrls: ['./update-app-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    IonButton,
    IonContent,
    IonIcon,
    IonProgressBar,
    NgIf,
    TranslateModule,
    NgClass,
  ],
})
export class UpdateAppModalComponent implements AfterViewInit, OnDestroy {
  readonly #platformService = inject(PlatformService);
  readonly #modalController = inject(ModalController);
  readonly #statusBarService = inject(StatusBarService);
  readonly #updateAppService = inject(UpdateAppService);

  protected readonly progress: WritableSignal<number> = signal<number>(1);
  protected readonly overlays: WritableSignal<boolean> = signal<boolean>(false);
  private interval: ReturnType<typeof setTimeout> | null = null;
  private readonly duration: number = 5000;
  private readonly steps: number = 50;
  private readonly intervalTime: number = this.duration / this.steps;
  private readonly decrement: number = 1 / this.steps;
  @Input() public updateIsRequired: boolean = false;

  constructor() {
    if (environment.useStatusBarOverlays && this.#platformService.isAndroidApp) {
      this.#statusBarService.getInfo().then((info: StatusBarInfo) => {
        this.overlays.set(info.overlays);
      });
    }
  }

  ngAfterViewInit() {
    this.startProgressTimer();
  }

  ngOnDestroy() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  private startProgressTimer(): void {
    this.interval = setInterval(() => {
      const nextValue: number = Math.max(this.progress() - this.decrement, 0);
      this.progress.set(nextValue);

      if (nextValue === 0) {
        this.completeProgress();
      }
    }, this.intervalTime);
  }

  private completeProgress(): void {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  protected async downloadApp(): Promise<void> {
    await this.close('download');
  }

  protected async close(role: string): Promise<void> {
    if (role === 'download') {
      await this.#updateAppService.downloadApp();
    }

    if (!this.updateIsRequired) {
      const updateAppModal: HTMLIonModalElement = await this.#modalController.getTop();
      await updateAppModal?.dismiss();
    }
  }
}
